import { useState, type FormEvent } from 'react';
import axios from 'axios';
import { submitContact } from '../api/endpoints';
import { CONTACT_LIMITS, validateContact, type ContactFields } from '../utils/validation';

const EMPTY: ContactFields = { name: '', companyName: '', email: '', useCase: '' };

/**
 * The landing page's "Contact us" form - React twin of the CONTACT US section in
 * web/Pages/Index.cshtml. Rules live in Core (ContactRequestService); validateContact mirrors them.
 */
export function ContactSection() {
  const [fields, setFields] = useState<ContactFields>(EMPTY);
  const [website, setWebsite] = useState(''); // honeypot
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const [sending, setSending] = useState(false);

  const set = (key: keyof ContactFields) => (value: string) => setFields((f) => ({ ...f, [key]: value }));

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);

    const found = validateContact(fields);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSending(true);
    try {
      const { message } = await submitContact({ ...fields, website });
      setStatus({ ok: true, message });
      setFields(EMPTY);
    } catch (err) {
      const body = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string; errors?: Record<string, string> } | undefined)
        : undefined;
      if (body?.errors) setErrors(body.errors);
      setStatus({
        ok: false,
        message:
          axios.isAxiosError(err) && err.response?.status === 429
            ? 'Too many messages from your network. Please try again in a few minutes.'
            : body?.message ?? 'We could not send your message. Please email us at admin@inertiaid.com.',
      });
    } finally {
      setSending(false);
    }
  }

  const field = (key: keyof ContactFields, serverKey: string, label: string, input: React.ReactNode) => (
    <>
      <label className="form-label fw-medium" htmlFor={`contact-${key}`}>{label}</label>
      {input}
      {errors[serverKey] && <div className="text-danger small mt-1">{errors[serverKey]}</div>}
    </>
  );

  const cls = (serverKey: string) => `form-control${errors[serverKey] ? ' is-invalid' : ''}`;

  return (
    <section id="contact" style={{ padding: '80px 0' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <div className="text-center mb-4">
          <div className="section-label mb-2">Contact Us</div>
          <h2 className="section-title mb-3">Talk to Our Team</h2>
          <p className="text-body-secondary mx-auto" style={{ maxWidth: '560px' }}>
            Tell us about your use case and we&apos;ll get back to you within one business day.
          </p>
        </div>

        {status && (
          <div className={`alert ${status.ok ? 'alert-success' : 'alert-danger'}`} role="status">
            {status.message}
          </div>
        )}

        <div className="card">
          <div className="card-body p-4">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-3">
                <div className="col-md-6">
                  {field('name', 'Name', 'Name',
                    <input id="contact-name" className={cls('Name')} maxLength={CONTACT_LIMITS.name} autoComplete="name"
                      value={fields.name} onChange={(e) => set('name')(e.target.value)} />)}
                </div>
                <div className="col-md-6">
                  {field('companyName', 'CompanyName', 'Company name',
                    <input id="contact-companyName" className={cls('CompanyName')} maxLength={CONTACT_LIMITS.company}
                      autoComplete="organization" value={fields.companyName} onChange={(e) => set('companyName')(e.target.value)} />)}
                </div>
                <div className="col-12">
                  {field('email', 'Email', 'Work email',
                    <input id="contact-email" type="email" className={cls('Email')} maxLength={CONTACT_LIMITS.email}
                      autoComplete="email" value={fields.email} onChange={(e) => set('email')(e.target.value)} />)}
                </div>
                <div className="col-12">
                  {field('useCase', 'UseCase', 'Describe your use case',
                    <textarea id="contact-useCase" className={cls('UseCase')} rows={5} maxLength={CONTACT_LIMITS.useCaseMax}
                      placeholder="What you need to verify, expected monthly volume, countries, timeline..."
                      value={fields.useCase} onChange={(e) => set('useCase')(e.target.value)} />)}
                </div>
                {/* Honeypot: off-screen and skipped by keyboard and autofill; only bots fill it. */}
                <div style={{ position: 'absolute', left: '-10000px' }} aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" tabIndex={-1} autoComplete="off"
                    value={website} onChange={(e) => setWebsite(e.target.value)} />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary px-4" disabled={sending}>
                    {sending
                      ? <><span className="spinner-border spinner-border-sm me-2" />Sending...</>
                      : <><i className="bi bi-send me-2" />Send message</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
